import { Box, Pagination, Typography } from '@mui/material';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { gql } from '@apollo/client';
import Banner from '@components/molecules/Banner';
import Container from '@components/atoms/Container';
import PageInsetTop from '@components/atoms/PageInsetTop';
import DefaultTemplate from '@components/templates/DefaultTemplate';
import JobPostingPreviewItem from '@components/molecules/JobPostingPreviewItem';
import { getAppData } from '@lib/appInitialState';
import { useTranslation } from 'next-i18next';

export default function JobPosting({
  appData,
  jobPostingItems,
  currentPage,
  pageCount,
}) {
  const { t } = useTranslation();
  const router = useRouter();

  const handleChangePage = (event, page) => {
    router.push(`/jobs?p=${page}`);
  };

  return (
    <>
      <Head>
        <title>
          {t('pages.jobs.home.pageTitle')}
        </title>
        <meta
          content={t('pages.jobs.home.metaDescription')}
          name="description"
        />
        <meta content={t('pages.jobs.home.pageTitle')} property="og:title" />
      </Head>
      <DefaultTemplate>
        <PageInsetTop />
        <Container>
          <Banner appData={appData} />
          <Box mt={3}>
            <Typography variant="h1">
              {t('pages.jobs.home.title1')}
            </Typography>
          </Box>
          <Box mt={5}>
            {jobPostingItems.map((item) => (
              <Box key={item.id} mb={5}>
                <JobPostingPreviewItem item={item} />
              </Box>
            ))}
          </Box>
          <Box justifyContent="center" mb={10} mt={5} sx={{ display: { xs: 'flex', md: 'none' } }}>
            <Pagination
              boundaryCount={1}
              count={pageCount}
              onChange={handleChangePage}
              page={currentPage}
              siblingCount={0}
            />
          </Box>
          <Box justifyContent="center" mb={10} mt={5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Pagination
              count={pageCount}
              onChange={handleChangePage}
              page={currentPage}
            />
          </Box>
        </Container>
      </DefaultTemplate>
    </>
  );
}

JobPosting.propTypes = {};

export async function getServerSideProps(ctx) {
  const {
    query,
    res,
    locale,
  } = ctx;
  const [graphQlClient, appData] = await getAppData(ctx);
  const { p } = query;
  let page = parseInt(p, 10);

  if (Number.isNaN(page) || page < 1) {
    page = 1;
  }

  const start = (page - 1) * ITEMS_PER_PAGE;
  const limit = ITEMS_PER_PAGE;

  const {
    error,
    data,
  } = await graphQlClient.query({
    query: FETCH_JOB_POSTING_ITEMS,
    variables: {
      start,
      limit,
      locale,
    },
  });

  if (error) {
    throw error;
  }

  if (res) {
    res.setHeader('Cache-Control', 'public, max-age=3600');
  }

  return {
    props: {
      ...appData,
      jobPostingItems: data.pages.data,
      currentPage: page,
      // pageCount: Math.ceil(data.pagesConnection.aggregate.count / ITEMS_PER_PAGE),
      pageCount: data.pages.meta.pagination.pageCount,
    },
  };
}

const ITEMS_PER_PAGE = 5;

const FETCH_JOB_POSTING_ITEMS = gql`
  query GetJobPostingItems($start: Int!, $limit: Int!, $locale: I18NLocaleCode!) {
    pages(
      filters: {
        publishedAt: { null: false },
        pageType: { in: ["jobPosting"] }
      },
      locale: $locale,
      sort: [ "isPinnedToTop:desc", "publishDate:desc", "publishedAt:desc" ],
      pagination: {
        start: $start,
        limit: $limit
      }
    ) {
      data {
        ${JobPostingPreviewItem.graphQlSchema}
      }
      meta {
        pagination {
          total
          page
          pageSize
          pageCount
        }
      }
    }
  }
`;
